/**
 * Types for the Team Onboarding feature (the wizard where a team grants Vector
 * cross-account IAM roles). Pure TS — no React.
 */

import type { AccountEntry } from '../../api';

/** Deployment stage a granted account targets. */
export type OnboardingStage = 'beta' | 'gamma' | 'prod';

/** Per-account validation lifecycle. */
export type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';

/**
 * A draft account row in the wizard: the granted cross-account entry plus its
 * local id and validation state. `accountId`/`roleArn` etc. are strings (freely
 * editable) until validated / submitted.
 */
export interface AccountDraft {
  /** Local-only stable id for list keys and per-row updates. */
  id: string;
  accountId: string;
  stage: OnboardingStage;
  region: string;
  roleArn: string;
  /** Whether roleArn was auto-derived from accountId (so we keep auto-filling). */
  roleArnAuto: boolean;
  validation: ValidationStatus;
  /** Validation error message from the backend when invalid. */
  errorMessage?: string;
}

/** The required cross-account role naming convention Vector assumes. */
export const VECTOR_ROLE_NAME = 'VectorAccess-DO-NOT-DELETE';

/** The default region prefilled on a new account row. */
export const DEFAULT_REGION = 'us-east-1';

/** Builds the required role ARN for an account per the naming convention. */
export function roleArnForAccount(accountId: string): string {
  const id = accountId.trim();
  return `arn:aws:iam::${id || '<accountId>'}:role/${VECTOR_ROLE_NAME}`;
}

/** Narrows a validated {@link AccountDraft} into a wire {@link AccountEntry}. */
export function draftToEntry(draft: AccountDraft): AccountEntry {
  return {
    accountId: draft.accountId.trim(),
    stage: draft.stage,
    region: draft.region.trim(),
    roleArn: draft.roleArn.trim(),
  };
}

/** True when a draft has the minimum fields needed to validate / submit. */
export function isDraftComplete(draft: AccountDraft): boolean {
  return (
    draft.accountId.trim().length > 0 &&
    draft.region.trim().length > 0 &&
    draft.roleArn.trim().length > 0
  );
}
